import {Injectable} from '@angular/core';
import { RequestUserStatus } from '../../model/enum/request-user-status.enum';
import { CplUserRequest } from '../../model/cpl-user-request.model';
import { CplRequestItem } from '../../model/cpl-request-item.model';
import { RequestVerificationStatus } from '../../requests/enums/verification-status.enum';
import { AccessRoleHelper } from '../../model/helpers/access-role.helper';
import { DateHelpers, SessionService, User } from '@edl-development/edl-common';
import { BaseItemHelpersService } from '../base-item-helpers/base-item-helpers.service';
import { RosterPreferenceValidation } from 'src/app/model/request/roster-preference-validation.model';
import { CplRequestSummaryItem } from 'src/app/model/cpl-request-summary-item.model';
import { RequestItemPermissions } from '../../model/enum/request-item-permissions.enum';

@Injectable()
export class RequestHelpersService {
    constructor(
        private sessionService: SessionService,
        private baseItemHelpersService: BaseItemHelpersService
    ) {}

    private get currentUser() {
        return this.sessionService.user;
    }

    private hasPermission(requestItem: CplRequestItem, permission: RequestItemPermissions): boolean {
        return (requestItem?.Permissions ?? []).includes(permission);
    }

    canViewItem(requestItem: CplRequestItem): boolean {
        return this.hasPermission(requestItem, RequestItemPermissions.CAN_VIEW_ITEM);
    }

    canManageItemByPermission(requestItem: CplRequestItem): boolean {
        return this.hasPermission(requestItem, RequestItemPermissions.CAN_MANAGE_ITEM);
    }

    canEditItem(requestItem: CplRequestItem): boolean {
        return this.hasPermission(requestItem, RequestItemPermissions.CAN_EDIT_ITEM);
    }

    canRequestAccess(requestItem: CplRequestItem): boolean {
        return this.hasPermission(requestItem, RequestItemPermissions.CAN_REQUEST_ACCESS);
    }

    canTogglePrivacy(requestItem: CplRequestItem): boolean {
        return this.hasPermission(requestItem, RequestItemPermissions.CAN_TOGGLE_PRIVACY);
    }

    hasPrivateLimitedAccess(requestItem: CplRequestItem): boolean {
        return !!requestItem?.IsPrivate && !this.canViewItem(requestItem);
    }
    // ----------------------------------------------------------------------------------

    isFinalUserStatus(userStatus: RequestUserStatus) {
        return userStatus === RequestUserStatus.Approved || 
            userStatus === RequestUserStatus.Disapproved ||
            userStatus === RequestUserStatus.FollowupForgiven ||
            userStatus === RequestUserStatus.Deleted ||
            userStatus === RequestUserStatus.Completed ||
            userStatus === RequestUserStatus.Cancelled;
    }

    canCancelUserRequest(userRequest: CplUserRequest) {
        const nonCancellableStatuses = [
            RequestUserStatus.Disapproved,
            RequestUserStatus.FollowupForgiven,
            RequestUserStatus.Deleted,
            RequestUserStatus.Cancelled
        ];

        // Forgiving a follow-up completes the parent request, so the forgiven state lives only on
        // the follow-up — hence the second check (EDLP-3537).
        return !nonCancellableStatuses.includes(userRequest.UserStatus) &&
            userRequest.FollowupStatusID !== RequestUserStatus.FollowupForgiven;
    }

    isAwaitingFollowupUserStatus(userStatus: RequestUserStatus) {
        return userStatus === RequestUserStatus.PendingFollowUp ||
            userStatus === RequestUserStatus.FollowupExpiring ||
            userStatus === RequestUserStatus.FollowUpExpired;
    }

    /**
     * Whether the request's Follow-Up Stage has actually begun (submission approved,
     * follow-up initiated). Distinct from `CplUserRequest.HasFollowup`, which on list
     * surfaces only means the item is *configured* to require a follow-up — true even
     * while the submission is still awaiting approval. A Modify Date Notification can
     * only ever be sent once this returns true, so notification controls must gate on
     * this, not on `HasFollowup` (EDLP-3569). Mirrors the backend's
     * `UserRequestHelpers.AWAITING_FOLLOWUP_USER_STATUSES` plus `FollowupSubmitted`.
     */
    hasFollowUpStageBegun(userStatus: RequestUserStatus) {
        return this.isAwaitingFollowupUserStatus(userStatus) ||
            userStatus === RequestUserStatus.FollowupSubmitted;
    }

    isPendingSubmission(userStatus: RequestUserStatus) {
        return userStatus === RequestUserStatus.Draft ||
            userStatus ===RequestUserStatus.Reopened;
    }

    isPendingApproval(userStatus: RequestUserStatus) {
        return userStatus === RequestUserStatus.Submitted ||
            userStatus === RequestUserStatus.FollowupSubmitted;
    }

    isRequestItemActive(requestItem: CplRequestItem) {
        return requestItem.IsPublished && !requestItem.IsDeleted && !requestItem.IsTemplate && !requestItem.IsArchived;
    }

    isWithinAvailabilityDates = (requestItem: CplRequestItem) => {
        const isItemPublished = this.isRequestItemActive(requestItem);

        if (!requestItem.Availability) {
            return isItemPublished;
        }

        const startDate = DateHelpers.shortDateStringToDate(requestItem.StartDate)?.getTime();
        const endDate = DateHelpers.shortDateStringToDate(requestItem.EndDate)?.getTime();
        const today = new Date().setHours(0, 0, 0, 0);

        return isItemPublished && startDate && endDate && today >= startDate && today <= endDate;
    }

    canSubmitOnBehalfOfUser(requestItem: CplRequestItem, rosterPreferenceValidation: RosterPreferenceValidation, user: User) {
        return this.canManageItem(requestItem, user) && this.isRequestItemActive(requestItem) && !rosterPreferenceValidation?.HasMaxSeatLimitBeenReached;
    }

    canManageItem(requestItem: CplRequestItem, user: User) {
        return requestItem.AssignedUsers.filter(u => u.Type != 0 && u.UserID == user.UserID).length > 0
            || this.isVerifier(requestItem, user)
            || requestItem.CreatedBy == user.UserID
            || AccessRoleHelper.isAdmin(user.AccessRoleID);
    }

    canSupervisorArchive(userRequest: CplUserRequest) {
        return this.isFinalUserStatus(userRequest.UserStatus) && userRequest.SupervisorArchived === false
    }

    userStatusAndFollowUpStatusToString(userStatus: RequestUserStatus, followUpUserStatus: RequestUserStatus) {
        if (userStatus === RequestUserStatus.Completed && followUpUserStatus === RequestUserStatus.FollowupForgiven) {
            return this.userStatusToString(followUpUserStatus);
        }

        return this.userStatusToString(userStatus);
    }

    userStatusToString(userStatus: RequestUserStatus) {
        switch(userStatus) {
            case RequestUserStatus.Draft:
                return 'Draft';
            case RequestUserStatus.Submitted:
                return 'Submitted';
            case RequestUserStatus.Reopened:
                return 'Reopened';
            case RequestUserStatus.Disapproved:
                return 'Disapproved';
            case RequestUserStatus.Approved:
                return 'Approved';
            case RequestUserStatus.PendingFollowUp:
                return 'Pending Follow-Up Submission';
            case RequestUserStatus.FollowupExpiring:
                return 'Follow-Up Expiring';
            case RequestUserStatus.FollowUpExpired:
                return 'Follow-Up Expired';
            case RequestUserStatus.FollowupForgiven:
                return 'Follow-Up Forgiven';
            case RequestUserStatus.FollowupSubmitted:
                return 'Follow-Up Submitted';
            case RequestUserStatus.Deleted:
                return 'Deleted';
            case RequestUserStatus.Completed:
                return 'Completed';
            case RequestUserStatus.Cancelled:
                return 'Cancelled';
            case RequestUserStatus.FollowUpRequired:
                return 'Follow-Up Required';
            case RequestUserStatus.FollowUpApproved:
                return 'Follow-Up Approved';
            default:
                return 'None'
        }
    }

    canPerformVerificationAction(userRequest: CplUserRequest, requestItem: CplRequestItem, action: RequestVerificationStatus) {
        if (!this.isRequestItemActive(requestItem)) {
            return false;
        }

        switch(action) {
            case RequestVerificationStatus.APPROVED:
                return userRequest.CanVerify && requestItem.IsPublished && userRequest.UserStatus != RequestUserStatus.Cancelled;
            case RequestVerificationStatus.CANCELLED:
                return this.canCancelUserRequest(userRequest);
            case RequestVerificationStatus.DISAPPROVED:
                return !this.isFinalUserStatus(userRequest.UserStatus)
                    && userRequest.UserStatus !== RequestUserStatus.Reopened
                    && userRequest.UserStatus !== RequestUserStatus.Draft;
            case RequestVerificationStatus.SKIPPED:
                return !this.isFinalUserStatus(userRequest.UserStatus) &&
                    !this.isAwaitingFollowupUserStatus(userRequest.UserStatus) &&
                    userRequest.VerificationStage &&
                    userRequest.VerificationStage.IsSkippable &&
                    userRequest.UserStatus !== RequestUserStatus.Reopened;
            case RequestVerificationStatus.REOPEN:
                return userRequest.UserStatus !== RequestUserStatus.Reopened
                    && userRequest.UserStatus !== RequestUserStatus.Draft
                    && userRequest.UserStatus !== RequestUserStatus.Deleted;
            case RequestVerificationStatus.FORGIVEN:
                return this.isAwaitingFollowupUserStatus(userRequest.UserStatus) || userRequest.UserStatus == RequestUserStatus.FollowupSubmitted;
            case RequestVerificationStatus.FOLLOWUP_DUE_DATE_CHANGED:
                return this.isAwaitingFollowupUserStatus(userRequest.UserStatus) || userRequest.UserStatus == RequestUserStatus.Submitted;
            default:
                return false;
        }
    }
    
    convertFollowupDueDateToLocal(date: any) {
        const dateToConvert = (date && typeof date == 'string') ? new Date(date) : date;

        if (!DateHelpers.isMinDate(dateToConvert)) {
            return new Date(dateToConvert.getFullYear(), dateToConvert.getMonth(), dateToConvert.getDate())
        }

        return date;
    }

    isVerifier(requestItem: CplRequestItem, user: User) {
        
        for (var i = 0; i < requestItem.VerificationStages.length; i++) { 
            if(requestItem.VerificationStages[i].Approvers.filter( u =>  u.UserID == user.UserID).length > 0){
                return true;
            }
        }
        for (var i = 0; i < requestItem.FollowupVerificationStages.length; i++) { 
            if(requestItem.FollowupVerificationStages[i].Approvers.filter( u =>  u.UserID == user.UserID).length > 0){
                return true;
            }
        }

        return false;
    }

    getAllApprovers(requestItem: CplRequestItem) {
        const stages = (requestItem.VerificationStages ?? []).concat(requestItem.FollowupVerificationStages ?? []);
        return this.baseItemHelpersService.getAllApprovers(stages);
    }

    getUserRequestVerificationStages(userRequest: CplUserRequest, isFollowup: boolean) {
        const followup = isFollowup ? 1 : 0;
        return (userRequest?.VerificationStages ?? [])
            .filter(v => v.Followup === followup);
    }

    public isAct48Enabled(requestItem: CplRequestItem): boolean {
        const portalSettings = this.sessionService?.PortalSettings;

        return portalSettings?.Act48Enabled 
            && requestItem != null 
            && requestItem.Act48Detail != null 
            && requestItem.Act48Detail.Applicable;
    }

    public canViewUserRequestPage(userRequest: CplUserRequest) {
        return this.isTargetOrManagingUser(userRequest) || AccessRoleHelper.isEDLAdmin(this.currentUser.AccessRoleID);
    }

    public isTargetOrManagingUser(userRequest: CplUserRequest) {
        return userRequest.UserID === this.currentUser.UserID || userRequest.ManagingUserID === this.currentUser.UserID;
    }
}